/**
 * The supplier panel on the rep's store page (2026-10-16, Helena — Blue Mango's merchandising checklist): for the
 * supplier picked in the store page's supplier row, five TICK boxes and the supplier's photos for THIS visit, in the
 * empty space next to the store name (under the supplier row on a phone). Same five questions for every supplier.
 *
 * A tick saves the moment it is ticked; photos upload the moment they are picked (Take photo / Gallery), tagged with
 * the supplier. Both go through `send` (MyRoute's offline queue: kept on the phone and sent when the signal is back).
 * Read only for an account that cannot edit (org admin / CAM viewing a rep's route).
 */
import { useEffect, useRef, useState } from "react"
import axios from "axios"
import { Box, Flex, Text, Checkbox, Button, Badge, Image, SimpleGrid } from "@chakra-ui/react"
import { FaCamera, FaImages, FaClipboardCheck } from "react-icons/fa"
import { neutral } from "../../theme/tokens"

const API = process.env.REACT_APP_API_URL

// One saved photo, fetched with the bearer token (the axios interceptor) and shown from a blob URL.
const Thumb = ({ imageId, body }) => {
  const [url, setUrl] = useState(null)
  useEffect(() => {
    let alive = true
    let made = null
    axios.post(`${API}/field/visit-photo`, { ...body, image_id: imageId }, { responseType: "blob" })
      .then((r) => { if (!alive) return; made = URL.createObjectURL(r.data); setUrl(made) })
      .catch(() => {})
    return () => { alive = false; if (made) URL.revokeObjectURL(made) }
  }, [imageId]) // eslint-disable-line react-hooks/exhaustive-deps
  return <Box w="52px" h="52px" borderRadius="6px" overflow="hidden" bg={neutral[100]} border="1px solid" borderColor={neutral[200]}>{url && <Image src={url} w="100%" h="100%" objectFit="cover" alt="" />}</Box>
}

const SupplierChecklist = ({ supplier, label, questions, state, pending = [], canEdit, onTick, onPhotos, uploading, body, maxPhotos = 10 }) => {
  const camera = useRef(null)
  const gallery = useRef(null)
  if (!supplier) {
    return (
      <Box className="mr-checklist" flex="0 1 420px" minW="260px" p={3} border="1px dashed" borderColor={neutral[300]} borderRadius="10px" bg={neutral[0]}>
        <Text fontSize="13px" color={neutral[500]}>Pick a supplier above for its checklist and photos.</Text>
      </Box>
    )
  }
  const s = state || {}
  const photos = (s.photos || 0) + pending.length
  const done = (questions || []).filter((q) => s[q.key]).length
  return (
    <Box className="mr-checklist" flex="0 1 420px" minW="260px" p={3} border="1px solid" borderColor="teal.200" borderRadius="10px" bg={neutral[0]}>
      <Flex align="center" justify="space-between" gap={2} mb={2}>
        <Flex align="center" gap={2} minW={0}>
          <Box as={FaClipboardCheck} color="teal.500" flexShrink={0} />
          <Text fontWeight={700} fontSize="14px" color={neutral[800]} noOfLines={1}>Checklist · {label || supplier}</Text>
        </Flex>
        <Badge colorScheme={done === (questions || []).length ? "green" : "gray"} flexShrink={0}>{done} / {(questions || []).length}</Badge>
      </Flex>
      <Box>
        {(questions || []).map((q) => (
          <Checkbox
            key={q.key} display="flex" size="lg" colorScheme="teal" py="5px" isChecked={!!s[q.key]} isReadOnly={!canEdit}
            onChange={(e) => canEdit && onTick(q.key, e.target.checked)}
            sx={{ ".chakra-checkbox__label": { fontSize: "14px", color: neutral[800], lineHeight: 1.25 } }}
          >
            {q.label}
          </Checkbox>
        ))}
      </Box>
      <Flex align="center" gap="6px" mt={2} wrap="wrap">
        {canEdit && (
          <>
            <Button size="sm" h="34px" px={2} fontSize="13px" variant="outline" colorScheme="purple" bg="white" leftIcon={<FaCamera />} iconSpacing={1}
              isLoading={uploading} loadingText="Uploading" isDisabled={photos >= maxPhotos} onClick={() => camera.current && camera.current.click()}>Take photo</Button>
            <Button size="sm" h="34px" px={2} fontSize="13px" variant="outline" colorScheme="purple" bg="white" leftIcon={<FaImages />} iconSpacing={1}
              isDisabled={uploading || photos >= maxPhotos} onClick={() => gallery.current && gallery.current.click()}>Gallery</Button>
            <input ref={camera} type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={(e) => { onPhotos(e.target.files); e.target.value = "" }} />
            <input ref={gallery} type="file" accept="image/*" multiple style={{ display: "none" }} onChange={(e) => { onPhotos(e.target.files); e.target.value = "" }} />
          </>
        )}
        <Text fontSize="12px" color={photos ? "green.600" : "orange.600"} fontWeight={600}>
          {photos ? `${photos} photo${photos === 1 ? "" : "s"}` : "No photo yet — a visit counts with a photo"}
        </Text>
      </Flex>
      {(s.images?.length > 0 || pending.length > 0) && (
        <SimpleGrid columns={6} spacing="6px" mt={2} maxW="360px">
          {(s.images || []).slice(-6).map((im) => <Thumb key={im.id} imageId={im.id} body={body} />)}
          {pending.map((p, i) => (
            <Box key={`p-${i}`} position="relative" w="52px" h="52px" borderRadius="6px" overflow="hidden" border="1px dashed" borderColor="orange.300">
              <Image src={p.url} w="100%" h="100%" objectFit="cover" opacity={0.6} alt="" />
              <Text position="absolute" bottom={0} w="100%" textAlign="center" fontSize="9px" bg="orange.400" color="white">waiting</Text>
            </Box>
          ))}
        </SimpleGrid>
      )}
    </Box>
  )
}

export default SupplierChecklist
